import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  ClubDocument,
  DocumentListQuery,
  DocumentListResponse,
  DocumentMetadataInput,
  DocumentMetaResponse,
} from '@jupi/shared'
import { api, apiFetch, apiUrl } from '../api/client'

// The club's documents, read side: shared by the member app's Documents page
// and the admin app's document manager.

const KEY = ['documents'] as const

export type DocumentFilter = Partial<Pick<DocumentListQuery, 'q' | 'category' | 'tag' | 'from' | 'to' | 'page' | 'pageSize'>> & {
  // Managers only: also the hidden documents. The API refuses it for anyone else.
  includeUnpublished?: boolean
}

function withQuery(path: string, params: Record<string, string | number | boolean | undefined>) {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) if (value !== undefined && value !== '') search.set(key, String(value))
  const qs = search.toString()
  return qs ? `${path}?${qs}` : path
}

export function useDocuments(filter: DocumentFilter) {
  return useQuery({
    queryKey: [...KEY, 'list', filter],
    queryFn: () => api.get<DocumentListResponse>(withQuery('/api/documents', filter)),
    // Keep the old page on screen while the next one loads, so typing in the
    // search box does not flash the list empty.
    placeholderData: (previous) => previous,
  })
}

export function useDocumentMeta() {
  return useQuery({
    queryKey: [...KEY, 'meta'],
    queryFn: () => api.get<DocumentMetaResponse>('/api/documents/meta'),
  })
}

// The file is streamed through the API, which checks the session; there is no
// other way to it.
export const documentDownloadUrl = (documentId: string) => apiUrl(`/api/documents/${documentId}/download`)

// ---------------------------------------------------------------------------
// Writes, for those who manage documents (the admin app)

export interface UploadDocumentInput {
  file: File
  metadata: DocumentMetadataInput
}

export function useDocumentWrites() {
  const queryClient = useQueryClient()
  // The lists, the counts per category, the tags and the years all move when a
  // document does, so every document query is refreshed together.
  const invalidate = () => queryClient.invalidateQueries({ queryKey: KEY })

  const upload = useMutation({
    mutationFn: ({ file, metadata }: UploadDocumentInput) => {
      const body = new FormData()
      body.append('file', file)
      body.append('metadata', JSON.stringify(metadata))
      return apiFetch<{ document: ClubDocument }>('/api/documents', { method: 'POST', body })
    },
    onSuccess: invalidate,
  })
  const update = useMutation({
    mutationFn: ({ documentId, patch }: { documentId: string; patch: Record<string, unknown> }) =>
      api.patch<{ document: ClubDocument }>(`/api/documents/${documentId}`, patch),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: (documentId: string) => api.delete(`/api/documents/${documentId}`),
    onSuccess: invalidate,
  })
  const retryArchive = useMutation({
    mutationFn: (documentId: string) => api.post(`/api/documents/${documentId}/archive/retry`, {}),
    onSuccess: invalidate,
  })
  return { upload, update, remove, retryArchive }
}
